import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, X } from "lucide-react";
import { InviteDialog } from "@/components/portal/InviteDialog";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useMe } from "@/components/portal/MeContext";
import { PageHeader } from "@/components/portal/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Users() {
  const api = useApi();
  const me = useMe();
  const [data, setData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviting, setInviting] = useState(false);
  useEffect(() => {
    api("get", "/users").then(setData);
    api("get", "/invites").then(setInvites);
  }, [api]);

  const onInvited = (inv) => {
    setInvites([inv, ...invites.filter((x) => x.email !== inv.email)]);
    if (inv.status === "accepted") api("get", "/users").then(setData);
  };
  const cancel = async (inv) => {
    try {
      await api("delete", `/invites/${inv.id}`);
      setInvites(invites.filter((x) => x.id !== inv.id));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pending = invites.filter((i) => i.status === "pending");

  const change = async (u, role) => {
    try {
      await api("patch", `/users/${u.user_id}/role`, { role });
      setData({ ...data, users: data.users.map((x) => (x.user_id === u.user_id ? { ...x, role } : x)) });
      toast.success(`${u.name} is now ${role}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader eyebrow="Access" title="People">
        <button onClick={() => setInviting(true)} data-testid="invite-btn" className="inline-flex items-center gap-2 rounded-full bg-[#F2B91C] px-5 py-2.5 text-sm font-bold text-[#0E1F3F] hover:bg-[#D9A30F]"><UserPlus size={16} />Invite</button>
      </PageHeader>
      <InviteDialog open={inviting} onOpenChange={setInviting} onInvited={onInvited} />
      {pending.length > 0 && (
        <div className="mb-6 rounded-2xl border border-[#E6E2DA] bg-white p-5" data-testid="pending-invites">
          <p className="eyebrow mb-3">Waiting to sign up</p>
          {pending.map((i) => (
            <div key={i.id} className="flex items-center justify-between border-b py-2 text-sm last:border-0" data-testid={`invite-row-${i.id}`}>
              <span><b>{i.name}</b> · {i.email} · <span className="capitalize">{i.role}</span></span>
              <span className="flex items-center gap-3">
                <button onClick={() => api("post", "/invites", { name: i.name, email: i.email, role: i.role }).then((r) => toast.success(r.email_sent ? "Invite re-sent" : "Could not send email")).catch((e) => toast.error(errMsg(e)))} className="text-xs font-semibold text-[#A67C00]" data-testid={`invite-resend-${i.id}`}>Resend</button>
                <button onClick={() => cancel(i)} className="text-[#8C877D] hover:text-red-600" aria-label="Cancel invite" data-testid={`invite-cancel-${i.id}`}><X size={15} /></button>
              </span>
            </div>
          ))}
        </div>
      )}
      <p className="mb-6 max-w-2xl text-sm text-[#5C5850]">Everyone who signs up starts as a <b>customer</b> and only sees projects, estimates and invoices sent to their email. Promote your crew to <b>employee</b> so they can clock in, see tasks and post updates.</p>
      <div className="overflow-x-auto rounded-2xl border border-[#E6E2DA] bg-white">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Joined</TableHead><TableHead className="w-44">Role</TableHead></TableRow></TableHeader>
          <TableBody>
            {data?.users.map((u) => (
              <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                <TableCell className="font-medium">{u.name}{u.user_id === me.user_id && " (you)"}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{fmtDate(u.created_at)}</TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(r) => change(u, r)}>
                    <SelectTrigger data-testid={`user-role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
